<template>
  <div :data-id="id" class="relative w-full">
    <div
      class="relative w-full cursor-pointer rounded border border-solid bg-gray-100 pl-2 pr-8 text-sm"
      :class="[
        !!isError
          ? 'border-[1.5px] border-red-400'
          : 'border-gray-600 hover:border-blue-400 focus:border-blue-400',
        model.length ? 'py-0.5' : 'py-1.5',
      ]"
    >
      <div ref="buttonRef" @click="toggleFold">
        <div
          v-if="model.length === 0"
          class="font-bold"
          :class="
            isDisabled ? 'text-gray-500' : 'text-blue-400 hover:text-blue-500'
          "
        >
          {{ title }}
        </div>
        <div v-else class="flex h-full flex-wrap items-center">
          <div v-for="item in selectedOptions" :key="item.code">
            <div
              class="relative my-0.5 mr-1 flex items-center rounded bg-gray-200 pb-0.5 pl-0.5 pr-2.5 pt-0.5"
            >
              <p class="grow basis-0 pl-1 pr-4 text-sm text-black">
                {{ item.name }}
              </p>
              <button
                class="absolute right-1 top-1 h-4 w-4 rounded-full bg-blue-400"
                @click.stop="handleRemoveType(item.code)"
              >
                <IconClose class="h-full w-full text-white" />
              </button>
            </div>
          </div>
        </div>
        <IconCaret
          :direction="!isFold ? 'up' : 'down'"
          class="absolute right-1 top-1 h-6 w-6 text-blue-400 hover:text-blue-500"
        />
      </div>
    </div>
    <div
      v-if="!isFold && !isDisabled"
      ref="dropdownRef"
      class="absolute left-0 top-[calc(100%+4px)] z-50 max-h-[200px] w-full overflow-y-auto rounded border border-solid border-gray-400 bg-gray-100 py-1 drop-shadow-lg"
    >
      <ul class="h-full w-full overflow-y-auto overflow-x-hidden">
        <li v-for="item in options" :key="item.code" class="mb-1 ml-2">
          <label class="flex cursor-pointer items-center" :for="item.code">
            <input
              :id="item.code"
              v-model="model"
              type="checkbox"
              class="mr-2 cursor-pointer"
              :value="item.code"
            />
            <p class="grow basis-0 text-sm text-blue-400">
              {{ item.name }}
            </p>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onClickOutside } from '@vueuse/core';

  interface IOption {
    name: string;
    code: string;
  }

  interface IProps {
    id: string;
    title: string;
    isDisabled?: boolean;
    isError?: boolean;
    options: IOption[];
  }

  const props = withDefaults(defineProps<IProps>(), {
    isDisabled: false,
    isError: false,
  });

  const emits = defineEmits<{
    (e: 'closed', value: boolean): void;
  }>();

  const model = defineModel<string[]>({ default: () => [] });

  const isFold = ref<boolean>(true);
  const dropdownRef = ref<HTMLElement | null>(null);
  const buttonRef = ref<HTMLElement | null>(null);

  onClickOutside(
    dropdownRef,
    () => {
      isFold.value = true;
      emits('closed', true);
    },
    { ignore: [buttonRef] }
  );

  const typeNameMap = computed(
    (): Map<string, string> =>
      new Map(props.options.map(({ code, name }) => [code, name]))
  );

  const selectedOptions = computed((): IOption[] =>
    model.value.map((code) => ({
      code,
      name: typeNameMap.value.get(code) ?? '',
    }))
  );

  const toggleFold = (): void => {
    if (props.isDisabled) {
      return;
    }
    isFold.value = !isFold.value;
    emits('closed', isFold.value);
  };

  const handleRemoveType = (type: string): void => {
    model.value = model.value.filter((code: string) => code !== type);
  };
</script>
